import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { galleryUrlLabel } from '../utils/galleryHost';
import SupportContact from './SupportContact';
import PolicyLinks from '../legal/PolicyLinks';

/**
 * `/` when no dev default gallery: explain how to open a gallery.
 * With REACT_APP_GALLERY_ID, redirect to `/g/:id` for local convenience.
 * Legacy portal iframe URLs used `/?token=&previewMode=true&galleryId=` — forward to `/g/:id`.
 */
export default function RootLanding() {
  const [searchParams] = useSearchParams();
  const previewMode = searchParams.get('previewMode') === 'true';
  const previewToken = searchParams.get('token');
  const legacyGalleryId = searchParams.get('galleryId')?.trim();
  if (previewMode && previewToken && legacyGalleryId) {
    const q = new URLSearchParams({ token: previewToken, previewMode: 'true' });
    return <Navigate to={`/g/${encodeURIComponent(legacyGalleryId)}?${q.toString()}`} replace />;
  }
  const devId = process.env.REACT_APP_GALLERY_ID;
  if (devId && String(devId).trim()) {
    return <Navigate to={`/g/${String(devId).trim()}`} replace />;
  }
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
      <main className="w-full max-w-md rounded-2xl border border-accent-30 bg-surface px-6 py-9 shadow-sm sm:px-10 sm:py-11">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary-10 text-primary" aria-hidden="true">
          <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
        </div>
        <h1 className="text-3xl font-serif text-secondary">WedLinx</h1>
        <p className="mt-3 text-base font-medium text-secondary">Your gallery begins with an invitation</p>
        <p className="mt-2 text-sm leading-relaxed text-secondary-70">
          Open the personal gallery link shared by the couple to view and add wedding memories.
        </p>
        <div className="mt-6 rounded-xl border border-accent-30 bg-accent-10 px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-secondary-70">Invitation link format</p>
          <p className="mt-1 break-all font-mono text-xs text-secondary">
            {galleryUrlLabel('your-gallery')}
          </p>
        </div>
        <p className="mt-5 text-xs leading-relaxed text-secondary-70">
          If your link is missing or no longer works, ask the couple to share it with you again.
        </p>
        <SupportContact />
        <div className="mt-6 border-t border-accent-30 pt-4">
          <PolicyLinks />
        </div>
      </main>
    </div>
  );
}
